import React, { useState, useEffect } from 'react';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { useUnderReviewStore } from 'src/store/under-review';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { NoWorriesStep } from '../no-worries/NoWorriesStep';
import { HumanReviewStep } from '../human-review/HumanReviewStep';
import { CongratulationsStep } from '../congratulations/CongratulationsStep';
import { ApiErrorStep } from '../api-error/ApiErrorStep';
import { finishStepsContent } from '../../content/content';
import { cn } from '@utils/index';
import { useApplicationStore } from 'src/store/application';

const components = {
  NoWorriesStep,
  HumanReviewStep,
  CongratulationsStep,
  ApiErrorStep,
} as any;

const DynamicComponent = ({
  componentName,
  handleNextCompatible,
}: {
  componentName: string;
  handleNextCompatible: () => void;
}) => {
  const Component = components[componentName] || 'NoWorriesStep';

  if (componentName === 'CongratulationsStep') {
    return (
      <Component
        question={{ question: "Let’s see the plans" }}
        onSubmit={handleNextCompatible}
      />
    );
  }

  return <Component />;
};

export const EstimateResult: React.FC<FormStepProps> = () => {
  const { isUnderReview } = useUnderReviewStore();
  const { underwritingResult, isUnderwritingLoading, isSubmittingApplication } =
    useFinalSubmissionStore();
  const { plans: chosenPlans } = usePlan();
  const { isLocalLoading, handlePreviousStep, handleJumpTo } = useFormControls();
  const [showCongratulations, setShowCongratulations] = useState(false);
  const {} = useApplicationStore
  const { phqUnderReviewAt, phqAcceptedAt } = useApplicationStore();

  const hasAnyPlan = !!chosenPlans.length;
  const finishStep = hasAnyPlan
    ? finishStepsContent.find((s) => s.id === 'congratulations')
    : finishStepsContent.find((s) => s.id === 'coverageReassurance');

  useEffect(() => {
    setShowCongratulations(underwritingResult?.status === 'accepted');
  }, [underwritingResult?.status]);

  type EstimateResultUI = {
    title?: string;
    description?: string;
    nextButtonLabel?: string;
    hideNextButton?: boolean;
    key?: string;
    componentName: 'NoWorriesStep' | 'HumanReviewStep' | 'CongratulationsStep' | 'ApiErrorStep';
    hidePrevButton?: boolean;
  };

  const ui: EstimateResultUI = React.useMemo(() => {
    if (underwritingResult?.status === 'human_review' || isUnderReview || phqUnderReviewAt) {
      return {
        title: `Application Review Required.`,
        description: '',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'HumanReviewStep',
        key: 'humanReview',
        hidePrevButton: true,
      };
    }

    if (showCongratulations || phqAcceptedAt) {
      return {
        title: 'Congratulations, you\'ve been approved for coverage!',
        description:
          "Nice work! With the tough stuff out of the way, your custom plan quotes are ready. If anything's unclear, Alia's on standby.<br /><br />Ready to shop?",
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'CongratulationsStep',
        key: 'congratulations',
        hidePrevButton: true,
      };
    }

    // Handle API errors - show technical error message
    if (underwritingResult?.status === 'rejected' && underwritingResult?.details?.reason === 'API Error') {
      return {
        title: 'Technical Error.',
        description: 'We\'re experiencing technical difficulties. Please try again.',
        nextButtonLabel: 'Try Again',
        hideNextButton: false,
        componentName: 'ApiErrorStep',
        key: 'apiError',
        hidePrevButton: true,
      };
    }

    // Handle rejected status - show message and allow to proceed
    if (underwritingResult?.status === 'rejected') {
      return {
        title: 'We\'re sorry, but we can\'t approve you for one of our health plans this time, but we can still protect you in other ways...',
        // description: 'Thank you for completing your health questionnaire. Let\'s continue with other coverage options.',
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'NoWorriesStep',
        key: 'rejected',
        hidePrevButton: true,
      };
    }

    if (!hasAnyPlan) {
      return {
        title: finishStep?.title,
        description: finishStep?.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoWorriesStep',
        key: 'noWorries',
        hidePrevButton: false,
      };
    }

    return {
      title: '',
      description: '',
      nextButtonLabel: undefined,
      hideNextButton: true,
      componentName: 'NoWorriesStep',
      key: 'noWorries',
      hidePrevButton: false,
    };
  }, [showCongratulations, hasAnyPlan, finishStep, underwritingResult, isUnderReview]);

  const { title, description, nextButtonLabel, hideNextButton, componentName, key, hidePrevButton } = ui;

  const handleNextCompatible = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();

    if (underwritingResult?.status === 'human_review') {
      return;
    }

    // Handle API error - go back to health questions to retry
    if (underwritingResult?.status === 'rejected' && underwritingResult?.details?.reason === 'API Error') {
      console.log('[EstimateResult] API error, navigating back to health questions to retry');
      handleJumpTo(FormStepSections.HealthQuestion);
      return;
    }

    if (nextButtonLabel === 'Next') {
      if (showCongratulations) {
        setShowCongratulations(false);
      }
      
      // Если rejected - удаляем medical plan и переходим сразу на Dental
      if (underwritingResult?.status === 'rejected') {
        console.log('[EstimateResult] User rejected, removing medical plan and navigating to Dental');
        useOnboardingStore.getState().removeMedicalPlan();
        handleJumpTo(FormStepSections.Dental);
        return;
      }
      
      // Для accepted или других статусов - переходим на Health Plans
      console.log('[EstimateResult] Navigating to Health Plans');
      handleJumpTo(FormStepSections.Medical);
      return;
    }
  };

  const handlePrev = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    
    if (underwritingResult?.status === 'human_review') {
      return;
    }
    
    if (showCongratulations) {
      return;
    }

    handlePreviousStep();
  };

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={handleNextCompatible}
      onPrev={hidePrevButton ? undefined : handlePrev}
      title={title}
      description={description}
      hideNextButton={hideNextButton}
      isSubmitText={false}
      onlyImage={!hasAnyPlan}
      isLoading={isUnderwritingLoading || isSubmittingApplication || isLocalLoading}
      nextButtonLabel={nextButtonLabel}
      headerAnimationKey={key}
    >
      <div className={cn('h-full w-full rounded-2xl')}>
        <div className="pb-4 h-full w-full justify-center">
          <DynamicComponent
            handleNextCompatible={handleNextCompatible}
            componentName={componentName}
          />
        </div>
      </div>
    </OnboardingFormStepLayout>
  );
};